import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import { useMutation, useQuery } from '@apollo/react-hooks';

import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import NumberIcon from 'components/number-icon/NumberIcon';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableSortLabel
} from '@material-ui/core';

import { EditIcon } from 'components/icons';
import { UPCOMING_ACCOMMODATIONS } from 'graphql/queries/overview';
import { CANCEL_ACCOMMODATION_BOOKING } from 'graphql/mutations/booking';
import { UpcomingAccommodationDialog } from 'components';
import {
  BookedAddOns,
  guestsSumSeats,
  isParticipantInfo,
  getAccommodationBookingTotalInfo,
  guestsDetailsInformation
} from 'utils/bookings/prepareBookingsData';
import ConfirmContext from 'contexts/ConfirmContext';
import ListPopover from 'components/list-popover/ListPopover';

import useStyles from './styles';
import { InfoOutlined } from '@material-ui/icons';

const headingsConfig = classes => {
  return [
    {
      label: 'Reference • Date',
      align: 'left',
      cellClassName: classes.referenceCell
    },
    {
      label: 'Booking name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Accommodation name',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'NO. of guests',
      sortBy: 'guests',
      cellClassName: classes.guestsCell
    },
    {
      label: 'Check-in • Check-out',
      align: 'left',
      sortBy: 'check_in_check_out',
      cellClassName: classes.checkInCheckOutCell
    },
    {
      label: 'Platform',
      sortBy: 'platform',
      cellClassName: classes.platformCell,
      align: 'left'
    },
    {
      label: 'Add-ons',
      cellClassName: classes.addOnsCell,
      sortBy: '',
      align: 'left'
    },
    {
      label: 'Notes',
      cellClassName: classes.notesCell,
      sortBy: '',
      align: 'left'
    },
    { label: 'Actions', align: 'right' }
  ];
};

function UpcomingAccommodations({ ownerId, upcomingPeriod }) {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('check_in_check_out');

  const [dialogContent, setDialogContent] = React.useState({});
  const { enqueueSnackbar } = useSnackbar();

  const { data, refetch } = useQuery(UPCOMING_ACCOMMODATIONS, {
    variables: {
      businessOwnerId: ownerId,
      orderBy: order === 'asc' ? orderBy : `-${orderBy}`,
      closestEventsPeriod: upcomingPeriod
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const confirm = React.useContext(ConfirmContext);
  const [cancelAccommodationBooking] = useMutation(
    CANCEL_ACCOMMODATION_BOOKING
  );

  const onClose = () => {
    setOpen(false);
  };
  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };
  const accommodationCancellationHandler = item => {
    confirm({
      title: `${t('Cancel Booking')}?`,
      warningText: `${
        item.relatedActivitiesBookings.length && !item.isRelated
          ? t('Please note that related booking also will be deleted')
          : ''
      }`,
      description: `${item.fromDate} — ${item.toDate} • ${item.guestFirstName} ${item.guestLastName} • ${item.accommodation.title}`,
      confirmationText: t('Cancel Booking'),
      cancellationText: t('Back')
    }).then(() =>
      cancelAccommodationBooking({ variables: { id: item.id } }).then(() => {
        enqueueSnackbar(t('Accommodation booking was successfully canceled!'), {
          variant: 'success'
        });
        refetch();
      })
    );
  };
  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                style={{ padding: '20px' }}
                className={classNames(
                  classes.tableCell,
                  classes.heading,
                  heading.cellClassName
                )}
              >
                <TableSortLabel
                  active={orderBy === heading.sortBy && orderBy !== ''}
                  direction={orderBy === heading.sortBy ? order : 'asc'}
                  onClick={createSortHandler(heading.sortBy)}
                  className={classes.heading}
                  disabled={!heading.sortBy}
                  hideSortIcon={!heading.sortBy}
                >
                  {t(heading.label)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.accommodationsBookings &&
            data.accommodationsBookings.map((booking, index) => {
              return (
                <TableRow
                  key={index}
                  classes={{ root: classes.tableRow }}
                  onClick={e => {
                    if (booking.deactivationBooking) return;

                    e.stopPropagation();
                    openInfoDialog(booking);
                  }}
                  className={classNames({
                    [classes.disabledArea]: booking.deactivationBooking
                  })}
                >
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.referenceCell
                    )}
                    align="left"
                  >
                    <Typography>{booking.referenceNumber}</Typography>
                    <Typography>{booking.creationTime}</Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell, classes.nameCell)}
                    align="left"
                  >
                    <Typography>
                      {booking.deactivationBooking
                        ? 'Deactivation Booking'
                        : `${booking.guestFirstName} ${booking.guestLastName}`}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell, classes.unitCell)}
                    align="left"
                  >
                    <Typography>{booking.accommodation.title}</Typography>
                  </TableCell>
                  <TableCell
                    className={classNames(
                      classes.tableCell,
                      classes.guestsCell
                    )}
                    align="left"
                  >
                    <div className={classes.guestsContent}>
                      {booking.guests.length === 0 && `—`}
                      {booking.guests.length !== 0 &&
                        booking.guests.length <= 2 && (
                          <Typography className={classes.cellText}>
                            {guestsSumSeats(booking.guests)}
                            {isParticipantInfo(booking.guests) &&
                              ` (${guestsDetailsInformation(
                                booking.guests,
                                t
                              )})`}
                          </Typography>
                        )}
                      {booking.guests.length !== 0 &&
                        booking.guests.length > 2 && (
                          <Typography className={classes.cellText}>
                            {guestsSumSeats(booking.guests)} (
                            {guestsDetailsInformation(
                              booking.guests.slice(0, 2),
                              t
                            )}
                            ...
                          </Typography>
                        )}

                      {booking.guests.length !== 0 &&
                        booking.guests &&
                        booking.guests.length > 2 && (
                          <ListPopover
                            list={
                              <ul className={classes.list}>
                                {booking.guests.slice(2).map((guest, index) => {
                                  return (
                                    <li key={index}>
                                      {guest.guestsNumber} x{' '}
                                      {guest.participant[0].participant}
                                    </li>
                                  );
                                })}
                              </ul>
                            }
                          >
                            <NumberIcon number={booking.guests.length - 2} />
                          </ListPopover>
                        )}
                    </div>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.checkInCheckOutCell
                    )}
                    align="left"
                  >
                    <Typography>
                      {booking.closestEvent.date}
                      {', '}
                      {booking.closestEvent.event === 'Check-in'
                        ? moment(
                            booking.accommodation.checkInTime,
                            'HH:mm:ssZZ'
                          ).format('HH:mm')
                        : moment(
                            booking.accommodation.checkOutTime,
                            'HH:mm:ssZZ'
                          ).format('HH:mm')}
                      {' • '}
                      {t(booking.closestEvent.event)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.platformCell
                    )}
                    align="left"
                  >
                    {t(booking.platform)}
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.addOnsCell
                    )}
                    align="left"
                  >
                    {booking.bookedAddOns.length <= 2 && (
                      <Typography className={classes.addOnTitle}>
                        {BookedAddOns(booking.bookedAddOns)}
                      </Typography>
                    )}
                    {booking.bookedAddOns.length > 2 && (
                      <Typography className={classes.addOnTitle}>
                        {BookedAddOns(booking.bookedAddOns.slice(0, 2))} ...
                      </Typography>
                    )}

                    {booking.bookedAddOns.length > 2 && (
                      <ListPopover
                        list={
                          <ul className={classes.list}>
                            {booking.bookedAddOns.slice(2).map((o, index) => {
                              return (
                                <li key={index}>
                                  {o.addOnCount} x {o.addOn[0].title}
                                </li>
                              );
                            })}
                          </ul>
                        }
                      >
                        <NumberIcon number={booking.bookedAddOns.length - 2} />
                      </ListPopover>
                    )}
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5 }}
                    className={classNames(classes.tableCell, classes.notesCell)}
                    align="left"
                  >
                    <ListPopover
                      maxWidth={300}
                      list={
                        <div className={classes.list} style={{ minWidth: 120 }}>
                          {booking.bookingNotes ||
                            booking.activityNotes ||
                            'There are no notes yet.'}
                        </div>
                      }
                    >
                      <InfoOutlined />
                    </ListPopover>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classes.tableCell}
                    align="right"
                  >
                    <div
                      style={{ display: 'inline-flex', paddingRight: '15px' }}
                    >
                      <IconButton
                        className={classes.iconButton}
                        disableTouchRipple={true}
                        disableFocusRipple={true}
                        aria-label="edit"
                        onClick={e => {
                          e.stopPropagation();
                          history.push(
                            `/accommodations-bookings/${booking.id}/edit`
                          );
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        disableTouchRipple={true}
                        disableFocusRipple={true}
                        className={classes.iconButton}
                        aria-label="delete"
                        onClick={e => {
                          e.stopPropagation();
                          accommodationCancellationHandler(booking);
                        }}
                      >
                        <HighlightOffIcon className={classes.closeIcon} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          <UpcomingAccommodationDialog
            onClose={onClose}
            booking={dialogContent}
            total={getAccommodationBookingTotalInfo(dialogContent)}
            onCancel={accommodationCancellationHandler}
            open={open}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default UpcomingAccommodations;
